import { useEffect, useState, useCallback, useMemo } from "react";
import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, Copy, ClipboardText, LinkSimple, Storefront, X,
  ChatCircleDots, CaretUp, CaretDown, Warning, TextT, Target, Lightbulb, CheckCircle,
  Trophy, Question, Rocket, CopySimple,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import LandingPageSections from "@/components/LandingPageSections";
import LandingPageTheme from "@/components/LandingPageTheme";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const DEFAULT_SECTION_ORDER = ["hero", "problem", "solution", "benefits", "proof",
                                "testimonials", "faq", "cta"];

const DEFAULT_CONTENT = {
  hero:         { headline: "", subheadline: "" },
  problem:      { heading: "", body: "" },
  solution:     { heading: "", body: "" },
  benefits:     { heading: "", items: [] },
  proof:        { heading: "", title: "", description: "", metric: "" },
  testimonials: { heading: "", items: [] },
  faq:          { heading: "", items: [] },
  cta:          { headline: "", button_text: "" },
};

function _fresh_sections() {
  return DEFAULT_SECTION_ORDER.map((t, i) => ({
    section_id: `sec_${Math.random().toString(36).slice(2, 12)}`,
    section_type: t,
    enabled: t === "hero",
    order: i,
    content: { ...DEFAULT_CONTENT[t], items: DEFAULT_CONTENT[t].items ? [...DEFAULT_CONTENT[t].items] : undefined },
  }));
}

const emptyForm = {
  name: "", form_id: "", is_active: true, campaign_id: "",
  website_id: "",
  form_reveal_mode: "on_cta",
  assessment_questions: [],
  theme: null,
  sections: _fresh_sections(),
};

const DEFAULT_ASSESSMENT_QUESTIONS = [
  "What process takes up the most manual work in your business?",
  "Roughly how many enquiries or leads do you receive per month?",
  "Which systems do you currently use to manage this?",
  "What would you most like to automate first?",
];

export default function LandingPages() {
  const { currentOrg } = useAuth();
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  const [dupState, setDupState] = useState({ name: "", form_id: "", campaign_id: "" });
  const [duplicating, setDuplicating] = useState(false);
  // Template picker (Phase 1 create-flow choice).
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [pickTemplateFrom, setPickTemplateFrom] = useState(null);
  const [pickState, setPickState] = useState({ name: "", form_id: "", campaign_id: "" });
  const [cloning, setCloning] = useState(false);

  const openTemplatePicker = async () => {
    setTemplatePickerOpen(true);
    if (templates.length === 0) {
      setTemplatesLoading(true);
      try {
        const { data } = await api.get("/landing-page-templates");
        setTemplates(data?.templates || []);
      } catch { toast.error("Could not load templates"); }
      finally { setTemplatesLoading(false); }
    }
  };
  const chooseTemplate = (t) => {
    setPickTemplateFrom(t);
    setPickState({ name: t.name.replace(/^Bold |^Professional /, ""), form_id: "", campaign_id: "" });
  };
  const doCloneTemplate = async () => {
    if (!pickTemplateFrom) return;
    const name = pickState.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!pickState.form_id) { toast.error("Pick a form to embed"); return; }
    setCloning(true);
    try {
      const body = { name, form_id: pickState.form_id };
      if (pickState.campaign_id) body.campaign_id = pickState.campaign_id;
      const { data } = await api.post(
        `/landing-page-templates/${pickTemplateFrom.landing_page_id}/clone`, body);
      setPages((rs) => [data, ...rs]);
      toast.success(`Created "${data.name}" from template`);
      setPickTemplateFrom(null); setTemplatePickerOpen(false);
      setPickState({ name: "", form_id: "", campaign_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clone template");
    } finally { setCloning(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, f, c, w] = await Promise.all([
        api.get("/landing-pages"),
        api.get("/forms").catch(() => ({ data: [] })),
        api.get("/campaigns").catch(() => ({ data: [] })),
        api.get("/websites").catch(() => ({ data: [] })),
      ]);
      setPages(p.data); setForms(f.data || []); setCampaigns(c.data || []); setWebsites(w.data || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Landing Pages isn't enabled for this workspace");
      else toast.error("Could not load landing pages");
      setPages([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return pages;
    const q = query.toLowerCase();
    return pages.filter(x => (x.name || "").toLowerCase().includes(q));
  }, [pages, query]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, sections: _fresh_sections() }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.landing_page_id);
    const secs = Array.isArray(p.sections) && p.sections.length > 0
      ? p.sections.map(s => ({ ...s, content: { ...(s.content || {}) } }))
      : _fresh_sections();
    setForm({ name: p.name || "", form_id: p.form_id || "",
              is_active: !!p.is_active, campaign_id: p.campaign_id || "",
              website_id: p.website_id || "",
              form_reveal_mode: p.form_reveal_mode || "immediate",
              assessment_questions: Array.isArray(p.assessment_questions) ? [...p.assessment_questions] : [],
              theme: p.theme || null,
              sections: secs });
    setDialogOpen(true);
  };

  const openDuplicate = (p) => {
    setDuplicateFrom(p);
    setDupState({ name: `${p.name} (copy)`, form_id: p.form_id || "", campaign_id: "" });
  };

  const doDuplicate = async () => {
    if (!duplicateFrom) return;
    const name = dupState.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setDuplicating(true);
    try {
      const payload = { name };
      if (dupState.form_id) payload.form_id = dupState.form_id;
      if (dupState.campaign_id) payload.campaign_id = dupState.campaign_id;
      const { data } = await api.post(`/landing-pages/${duplicateFrom.landing_page_id}/duplicate`, payload);
      setPages((rs) => [data, ...rs]);
      toast.success(`Duplicated "${duplicateFrom.name}" → "${data.name}"`);
      setDuplicateFrom(null);
      setDupState({ name: "", form_id: "", campaign_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    } finally { setDuplicating(false); }
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!form.form_id) { toast.error("Please pick a form to embed"); return; }
    // Hero headline is the only hard requirement on create (server enforces).
    const hero = form.sections.find(s => s.section_type === "hero");
    const headline = (hero?.content?.headline || "").trim();
    if (!headline) { toast.error("Hero section needs a headline"); return; }
    const subheadline = (hero?.content?.subheadline || "").trim();
    // Legacy field mirrors — keeps existing indexing/attribution safe.
    const benefitsSection = form.sections.find(s => s.section_type === "benefits");
    const testimonialsSection = form.sections.find(s => s.section_type === "testimonials");
    const payload = {
      name, form_id: form.form_id, is_active: form.is_active,
      headline, subheadline,
      benefits: benefitsSection?.content?.items || [],
      testimonials: testimonialsSection?.content?.items || [],
      sections: form.sections,
      // Explicit clear when the user chose "— None —" (empty string).
      campaign_id: form.campaign_id || "",
      website_id: form.website_id || "",
      form_reveal_mode: form.form_reveal_mode || "on_cta",
      // Only send assessment_questions when the page is actually in assessment
      // mode. On other modes we send [] to keep DB clean & fall back to defaults.
      assessment_questions: form.form_reveal_mode === "ai_assessment"
        ? (form.assessment_questions || [])
        : [],
      // Theme: null (or all-empty object) means "keep defaults". Server treats
      // an all-blank payload as null so nothing changes on unset pages.
      theme: form.theme || {},
    };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/landing-pages/${editing}`, payload);
        setPages(all => all.map(x => x.landing_page_id === editing ? data : x));
        toast.success("Landing page updated");
      } else {
        const { data } = await api.post("/landing-pages", payload);
        setPages(all => [data, ...all]);
        toast.success("Landing page created");
      }
      setDialogOpen(false); setEditing(null);
      setForm({ ...emptyForm, sections: _fresh_sections() });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/landing-pages/${confirmDelete.landing_page_id}`);
      setPages(all => all.filter(x => x.landing_page_id !== confirmDelete.landing_page_id));
      toast.success(`Removed "${confirmDelete.name}"`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  const publicUrlFor = (slug) => `${window.location.origin}/p/${slug}`;
  const copyUrl = async (slug) => {
    try { await navigator.clipboard.writeText(publicUrlFor(slug)); toast.success("Public URL copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="landing-pages-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Landing Pages</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-xl">
            Modular pages — build them from toggleable sections. Wraps an existing Form so attribution flows through the linked campaign.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="lp-add-from-template" onClick={openTemplatePicker}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 text-ink-primary px-4 py-2.5 text-sm hover:border-white/25 hover:bg-white/[0.04]">
            <CopySimple weight="bold" className="h-4 w-4" /> Start from template
          </button>
          <button data-testid="lp-add" onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200">
            <Plus weight="bold" className="h-4 w-4" /> New landing page
          </button>
        </div>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input data-testid="lp-search" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by name"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30" />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading landing pages…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="lp-empty">
            <Storefront weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {pages.length === 0 ? "No landing pages yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {pages.length === 0 ? "Create your first landing page — it needs a Form to capture leads." : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lp-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Linked form</th>
                  <th className="px-5 py-3 font-medium">Campaign</th>
                  <th className="px-5 py-3 font-medium">Sections</th>
                  <th className="px-5 py-3 font-medium">Public URL</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(p => {
                  const enabledCount = (p.sections || []).filter(s => s.enabled).length;
                  return (
                    <tr key={p.landing_page_id} data-testid={`lp-row-${p.landing_page_id}`} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-ink-primary" data-testid={`lp-name-${p.landing_page_id}`}>{p.name}</td>
                      <td className="px-5 py-3 text-ink-secondary text-xs">{p.form_name || "—"}</td>
                      <td className="px-5 py-3 text-ink-secondary text-xs">{p.campaign_name || "—"}</td>
                      <td className="px-5 py-3 text-ink-secondary text-xs tabular-nums" data-testid={`lp-sections-count-${p.landing_page_id}`}>
                        {enabledCount} of {(p.sections || []).length}
                      </td>
                      <td className="px-5 py-3">
                        <div className="inline-flex items-center gap-2 text-xs font-mono text-ink-secondary max-w-[260px]">
                          <LinkSimple weight="bold" className="h-3 w-3 shrink-0" />
                          <span data-testid={`lp-slug-${p.landing_page_id}`} className="truncate">/p/{p.public_slug}</span>
                          <button data-testid={`lp-copy-${p.landing_page_id}`} onClick={() => copyUrl(p.public_slug)}
                            className="text-ink-secondary hover:text-ink-primary" title="Copy full URL">
                            <Copy weight="bold" className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {p.is_active ? (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/5 text-emerald-200">Active</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-ink-secondary/70">Inactive</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button data-testid={`lp-duplicate-${p.landing_page_id}`} onClick={() => openDuplicate(p)}
                            title="Duplicate"
                            className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><CopySimple weight="bold" className="h-3.5 w-3.5" /></button>
                          <button data-testid={`lp-edit-${p.landing_page_id}`} onClick={() => openEdit(p)}
                            className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><PencilSimple weight="bold" className="h-3.5 w-3.5" /></button>
                          <button data-testid={`lp-delete-${p.landing_page_id}`} onClick={() => setConfirmDelete(p)}
                            className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"><Trash weight="bold" className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm({ ...emptyForm, sections: _fresh_sections() }); setEditing(null); } } }}>
        <DialogContent data-testid="lp-dialog" className="max-w-4xl bg-graphite-900 border-white/10 text-ink-primary max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit landing page" : "New landing page"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Toggle sections on/off, reorder with the arrows, and edit content inline. Only enabled sections render on the public page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Tabs defaultValue="content" data-testid="lp-edit-tabs">
              <TabsList className="grid grid-cols-2 bg-graphite-950 border border-white/10">
                <TabsTrigger value="content" data-testid="lp-tab-content">Content</TabsTrigger>
                <TabsTrigger value="theme"   data-testid="lp-tab-theme">Theme</TabsTrigger>
              </TabsList>
              <TabsContent value="content" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Internal name</label>
                <Input data-testid="lp-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={200} placeholder="e.g. Q1 Ops-Vp Landing" className="bg-graphite-950 border-white/10" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Form to embed</label>
                <select data-testid="lp-form-formid" value={form.form_id}
                  onChange={e => setForm({ ...form, form_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— Pick a form —</option>
                  {forms.map(f => <option key={f.form_id} value={f.form_id}>{f.name}{!f.is_active ? " (inactive)" : ""}</option>)}
                </select>
                <p className="text-[11px] text-ink-secondary/60">Leads captured here use the linked form's submission logic, source label and campaign attribution.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Campaign (optional)</label>
                <select data-testid="lp-form-campaign" value={form.campaign_id}
                  onChange={e => setForm({ ...form, campaign_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-ink-secondary/60">Direct link. Separate from the form's own campaign attribution — used for filtering & finding pages by campaign.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Website (optional — enables GA4)</label>
                <select data-testid="lp-form-website" value={form.website_id}
                  onChange={e => setForm({ ...form, website_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {websites.map(w => <option key={w.website_id} value={w.website_id}>{w.name}{w.tracking_status !== "configured" ? " (no Measurement ID)" : ""}</option>)}
                </select>
                {(() => {
                  const w = websites.find(x => x.website_id === form.website_id);
                  if (!form.website_id) {
                    return <p data-testid="lp-form-ga4-status" data-status="none"
                              className="text-[11px] text-ink-secondary/60">GA4 tracking: not connected. Link a website to inject gtag.js automatically.</p>;
                  }
                  if (w && w.tracking_status === "configured") {
                    return <p data-testid="lp-form-ga4-status" data-status="inherited"
                              className="text-[11px] text-emerald-300/90">GA4 tracking: inherited from <strong>{w.name}</strong> ({w.ga4_measurement_id}).</p>;
                  }
                  return <p data-testid="lp-form-ga4-status" data-status="not-configured"
                            className="text-[11px] text-amber-300/90">GA4 tracking: website linked, but no Measurement ID set — add one on the Websites page.</p>;
                })()}
              </div>
            </div>

            <LandingPageSections
              sections={form.sections}
              onChange={(newSections) => setForm(prev => ({ ...prev, sections: newSections }))}
            />

            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4 space-y-2"
                  data-testid="lp-form-reveal-mode-block">
              <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary">Form reveal</div>
              <p className="text-[11px] text-ink-secondary/70">
                Controls when the embedded form becomes visible. "On CTA click" leads with the value
                proposition and reveals the form after the visitor engages — improves lead quality.
              </p>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                {[
                  { value: "immediate", label: "Immediate — always visible" },
                  { value: "on_cta", label: "On CTA click — reveal on engagement" },
                  { value: "ai_assessment", label: "AI Assessment — qualify first" },
                ].map(opt => (
                  <button
                    type="button"
                    key={opt.value}
                    data-testid={`lp-form-reveal-mode-${opt.value}`}
                    data-active={form.form_reveal_mode === opt.value ? "true" : "false"}
                    onClick={() => setForm({ ...form, form_reveal_mode: opt.value })}
                    className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${
                      form.form_reveal_mode === opt.value
                        ? "border-white/40 bg-white text-graphite-950"
                        : "border-white/10 bg-graphite-900 text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.form_reveal_mode === "ai_assessment" && (
                <AssessmentQuestionsEditor
                  value={form.assessment_questions}
                  defaults={DEFAULT_ASSESSMENT_QUESTIONS}
                  onChange={(next) => setForm({ ...form, assessment_questions: next })}
                />
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div>
                <div className="text-sm text-ink-primary">Active</div>
                <div className="text-[11px] text-ink-secondary">Inactive pages return "Not found" at the public URL.</div>
              </div>
              <Switch data-testid="lp-form-active" checked={form.is_active}
                onCheckedChange={v => setForm({ ...form, is_active: v })} />
            </div>

            {editing && (
              <div className="rounded-md border border-white/10 bg-graphite-950 p-3">
                <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1.5">
                  <ClipboardText weight="duotone" className="h-3 w-3" /> Public URL
                </div>
                <div className="mt-1 font-mono text-xs text-ink-primary break-all">{publicUrlFor(pages.find(x => x.landing_page_id === editing)?.public_slug || "")}</div>
              </div>
            )}
              </TabsContent>
              <TabsContent value="theme" className="mt-4">
                <LandingPageTheme
                  value={form.theme}
                  onChange={(next) => setForm(prev => ({ ...prev, theme: next }))}
                />
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <button data-testid="lp-form-cancel" onClick={() => { if (!saving) { setDialogOpen(false); setEditing(null); setForm({ ...emptyForm, sections: _fresh_sections() }); } }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="lp-form-save" onClick={save} disabled={saving || !form.name.trim() || !form.form_id}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Create landing page"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="lp-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove landing page?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted and its public URL will stop working immediately. The linked form is untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lp-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="lp-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!duplicateFrom} onOpenChange={(v) => { if (!duplicating && !v) { setDuplicateFrom(null); setDupState({ name: "", form_id: "", campaign_id: "" }); } }}>
        <DialogContent data-testid="lp-duplicate-dialog" className="bg-graphite-900 border-white/10 text-ink-primary max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">Duplicate landing page</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Copies every section and its content into a new, fully editable landing page.
              Editing the copy will never affect the original.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-white/10 bg-graphite-950 p-3 text-xs text-ink-secondary" data-testid="lp-duplicate-source">
              Source · <span className="text-ink-primary">{duplicateFrom?.name}</span>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">New name</label>
              <Input data-testid="lp-duplicate-name" value={dupState.name} maxLength={200}
                onChange={e => setDupState({ ...dupState, name: e.target.value })}
                className="bg-graphite-950 border-white/10" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Form to embed</label>
              <select data-testid="lp-duplicate-formid" value={dupState.form_id}
                onChange={e => setDupState({ ...dupState, form_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— Same as source —</option>
                {forms.map(f => <option key={f.form_id} value={f.form_id}>{f.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Campaign (optional)</label>
              <select data-testid="lp-duplicate-campaign" value={dupState.campaign_id}
                onChange={e => setDupState({ ...dupState, campaign_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None (cleared by default) —</option>
                {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
              </select>
              <p className="text-[11px] text-ink-secondary/60">Cleared by default so you consciously assign the copy to its own campaign.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <button data-testid="lp-duplicate-cancel" onClick={() => { if (!duplicating) { setDuplicateFrom(null); setDupState({ name: "", form_id: "", campaign_id: "" }); } }} disabled={duplicating}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="lp-duplicate-confirm" onClick={doDuplicate} disabled={duplicating || !dupState.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {duplicating ? "Duplicating…" : "Duplicate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template picker dialog — Phase 1 "Start from template" flow. */}
      <Dialog open={templatePickerOpen} onOpenChange={(v) => { if (!cloning) { setTemplatePickerOpen(v); if (!v) { setPickTemplateFrom(null); setPickState({ name: "", form_id: "", campaign_id: "" }); } } }}>
        <DialogContent data-testid="lp-template-picker" className="max-w-3xl bg-graphite-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {pickTemplateFrom ? `Start from "${pickTemplateFrom.name}"` : "Start from a template"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary">
              {pickTemplateFrom
                ? "Give the new page a name and pick a form. Everything else can be edited after."
                : "Templates are curated starting points — you can change everything after cloning."}
            </DialogDescription>
          </DialogHeader>

          {!pickTemplateFrom && (
            <div className="space-y-3">
              {templatesLoading ? (
                <div className="p-8 text-center text-sm text-ink-secondary" data-testid="lp-templates-loading">Loading templates…</div>
              ) : templates.length === 0 ? (
                <div className="p-8 text-center text-sm text-ink-secondary" data-testid="lp-templates-empty">No templates available yet.</div>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lp-templates-list">
                  {templates.map((t) => (
                    <li key={t.landing_page_id}>
                      <button type="button"
                        data-testid={`lp-template-${t.landing_page_id}`}
                        onClick={() => chooseTemplate(t)}
                        className="w-full text-left rounded-md border border-white/10 bg-graphite-950 p-4 hover:border-white/25 hover:bg-white/[0.03] transition-colors">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
                          {t.template_category || "Uncategorized"}
                        </div>
                        <div className="mt-1 font-display text-lg tracking-tight text-ink-primary">{t.name}</div>
                        <p className="mt-1.5 text-xs text-ink-secondary line-clamp-2">{t.subheadline || t.headline}</p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {(t.sections || []).filter(s => s.enabled).slice(0, 6).map((s, i) => (
                            <span key={i} className="text-[10px] uppercase tracking-widest text-ink-secondary/70 border border-white/10 rounded-full px-2 py-0.5">
                              {s.section_type.replace("_", " ")}
                            </span>
                          ))}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {pickTemplateFrom && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">New landing page name</label>
                <Input data-testid="lp-template-clone-name" value={pickState.name} maxLength={200}
                  onChange={e => setPickState({ ...pickState, name: e.target.value })}
                  className="bg-graphite-950 border-white/10" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Linked form <span className="text-accent-danger">*</span></label>
                <select data-testid="lp-template-clone-form" value={pickState.form_id}
                  onChange={e => setPickState({ ...pickState, form_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— Pick a form —</option>
                  {forms.map(f => <option key={f.form_id} value={f.form_id}>{f.name}</option>)}
                </select>
                <p className="text-[11px] text-ink-secondary/60">Templates ship without a form — the clone needs one of yours.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Campaign (optional)</label>
                <select data-testid="lp-template-clone-campaign" value={pickState.campaign_id}
                  onChange={e => setPickState({ ...pickState, campaign_id: e.target.value })}
                  className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                  <option value="">— None —</option>
                  {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-2">
            {pickTemplateFrom && (
              <button data-testid="lp-template-back" onClick={() => setPickTemplateFrom(null)} disabled={cloning}
                className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
                Back
              </button>
            )}
            <button data-testid="lp-template-cancel" onClick={() => { if (!cloning) { setTemplatePickerOpen(false); setPickTemplateFrom(null); setPickState({ name: "", form_id: "", campaign_id: "" }); } }} disabled={cloning}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
              Cancel
            </button>
            {pickTemplateFrom && (
              <button data-testid="lp-template-clone-confirm" onClick={doCloneTemplate}
                disabled={cloning || !pickState.name.trim() || !pickState.form_id}
                className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
                {cloning ? "Creating…" : "Create landing page"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// AssessmentQuestionsEditor — reuses the same "list with add/remove/reorder"
// pattern the FAQ + Benefits section editors use. Rendered only when the page
// is in ai_assessment mode.
function AssessmentQuestionsEditor({ value, defaults, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const usingCustom = items.length > 0;
  const displayItems = usingCustom ? items : defaults;

  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };
  const startCustom = () => onChange([...defaults]);
  const resetToDefaults = () => onChange([]);
  // Always-editable — appends an empty question directly; user types into
  // the new row inline. No staging draft that could be lost on outer Save.
  const addBlank = () => {
    if (items.length >= 6) return;
    onChange([...items, ""]);
  };

  return (
    <div className="mt-4 rounded-md border border-white/10 bg-graphite-900 p-4 space-y-3"
         data-testid="lp-assessment-questions-editor"
         data-mode={usingCustom ? "custom" : "defaults"}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary">Assessment questions</div>
          <p className="text-[11px] text-ink-secondary/70 mt-1">
            {usingCustom
              ? `Using your ${items.length} custom question${items.length === 1 ? "" : "s"} (max 6).`
              : "Using the 4 default questions. Customize to override — reorder, edit, add or remove."}
          </p>
        </div>
        {usingCustom ? (
          <button type="button"
                  data-testid="lp-assessment-questions-reset"
                  onClick={resetToDefaults}
                  className="rounded-full border border-white/10 bg-graphite-950 px-3 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5">
            Reset to defaults
          </button>
        ) : (
          <button type="button"
                  data-testid="lp-assessment-questions-customize"
                  onClick={startCustom}
                  className="rounded-full bg-white text-graphite-950 px-3 py-1 text-[11px] font-medium hover:bg-neutral-100">
            Customize
          </button>
        )}
      </div>

      <ol className="space-y-2">
        {displayItems.map((q, i) => (
          <li key={i} data-testid={`lp-assessment-question-${i}`}
              className="rounded-md border border-white/10 bg-graphite-950 p-3 flex items-start gap-2">
            <span className="mt-1 text-[10px] uppercase tracking-widest text-ink-secondary tabular-nums">
              Q{i + 1}
            </span>
            {usingCustom ? (
              <input
                data-testid={`lp-assessment-question-${i}-input`}
                value={q}
                maxLength={300}
                placeholder="Type your question…"
                onChange={(e) => {
                  const next = [...items];
                  next[i] = e.target.value;
                  onChange(next);
                }}
                className="flex-1 h-8 rounded-md bg-graphite-900 border border-white/10 px-2 text-sm text-ink-primary focus:outline-none focus:border-white/30"
              />
            ) : (
              <span className="flex-1 text-sm text-ink-primary">{q}</span>
            )}
            {usingCustom && (
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" data-testid={`lp-assessment-question-${i}-up`}
                        onClick={() => move(i, -1)} disabled={i === 0}
                        className="p-1 rounded text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent">
                  <CaretUp weight="bold" className="h-3 w-3" />
                </button>
                <button type="button" data-testid={`lp-assessment-question-${i}-down`}
                        onClick={() => move(i, 1)} disabled={i === items.length - 1}
                        className="p-1 rounded text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent">
                  <CaretDown weight="bold" className="h-3 w-3" />
                </button>
                <button type="button" data-testid={`lp-assessment-question-${i}-remove`}
                        onClick={() => remove(i)}
                        className="p-1 rounded text-ink-secondary hover:text-accent-danger hover:bg-white/5">
                  <X weight="bold" className="h-3 w-3" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>

      {usingCustom && items.length < 6 && (
        <button type="button"
                data-testid="lp-assessment-questions-add"
                onClick={addBlank}
                className="rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium hover:bg-neutral-100">
          + Add another question
        </button>
      )}
    </div>
  );
}
